import React, { useState, useRef } from 'react'
import { useBlockEffect } from '@smartfire/js-block-engine'
import { useMediaQuery } from '../../hooks'
import { Ad } from '../../types'
import clsx from 'clsx'
import style from './style.module.scss'

declare global {
    interface Window {
        dataLayer: any[]
    }
}

interface ComponentProps {
    ads: Ad[]
    taxonomySlug: string | null
    minHeight?: number
    isSpace?: boolean
    desktopOnly?: boolean
    mobileOnly?: boolean
    isEditing?: boolean
}

const AdComponent: React.FC<ComponentProps> = ({
    ads,
    taxonomySlug,
    minHeight = 250,
    isSpace = false,
    desktopOnly = false,
    mobileOnly = false,
    isEditing = false,
}) => {
    const isMobile = useMediaQuery(`(max-width: ${style.mobileBreakpoint})`)
    const adRef = useRef<HTMLDivElement>(null)
    const [selectedAd, setSelectedAd] = useState<Ad | null>(null)

    const getRandomAd = (ads: Ad[]) => {
        const totalWeight = ads.reduce((sum, ad) => sum + ad.weight, 0)
        let random = Math.random() * totalWeight
        let randomAd: Ad | null = null

        for (const ad of ads) {
            if (random < ad.weight) {
                randomAd = ad
                break
            }
            random -= ad.weight
        }

        return randomAd
    }

    // Choose ad based on the weight
    useBlockEffect(() => {
        if (!isEditing && ads.length > 0) {
            const randomAd = getRandomAd(ads)
            setSelectedAd(randomAd)

            // Send event to Google Analytics when the selected ad is loaded
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'ad_loaded',
                    event_category: 'ads',
                    event_label: randomAd?.name,
                    event_location: taxonomySlug,
                    non_interaction: true,
                })
            }
        }
    }, [isEditing, ads, taxonomySlug])

    useBlockEffect(() => {
        if (selectedAd && adRef.current) {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            // Send event to Google Analytics when ad is visible
                            if (window.dataLayer) {
                                window.dataLayer.push({
                                    event: 'ad_visible',
                                    event_category: 'ads',
                                    event_label: selectedAd.name,
                                    event_location: taxonomySlug,
                                    non_interaction: true,
                                })
                            }
                            observer.unobserve(entry.target)
                        }
                    })
                },
                { threshold: 0.5 },
            )

            observer.observe(adRef.current)

            return () => {
                observer.disconnect()
            }
        }

        return () => {} // Provide a fallback return statement
    }, [selectedAd, taxonomySlug])

    const handleAdClick = () => {
        if (selectedAd && window.dataLayer) {
            // Send event to Google Analytics when ad is clicked
            window.dataLayer.push({
                event: 'ad_clicked',
                event_category: 'ads',
                event_label: selectedAd.name,
                event_location: taxonomySlug,
                non_interaction: false,
            })
        }
    }

    const Tag = selectedAd && selectedAd.link ? 'a' : 'div'
    const tagProps =
        selectedAd && selectedAd.link
            ? {
                  href: selectedAd.link,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  onClick: handleAdClick,
              }
            : {}

    return (
        <div
            className={clsx(
                style.advertisement,
                isEditing && style.editing,
                !isEditing && ads.length === 0 && style.hide,
                isSpace && style.space,
                !isEditing && desktopOnly && style.desktopOnly,
                !isEditing && mobileOnly && style.mobileOnly,
            )}
            style={{ '--minHeight': `${minHeight}px` } as any}
            ref={adRef}
        >
            <Tag {...tagProps} className={style.wrapper}>
                {!isEditing && selectedAd && (
                    <img
                        src={
                            isMobile && selectedAd.images['mobile']
                                ? selectedAd.images['mobile'].url
                                : selectedAd.images['desktop'].url
                        }
                        alt={
                            isMobile && selectedAd.images['mobile']
                                ? selectedAd.images['mobile'].alt
                                : selectedAd.images['desktop'].alt
                        }
                    />
                )}

                {isEditing && <p>Une publicité s'affichera ici.</p>}
            </Tag>
        </div>
    )
}

export default AdComponent
